
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdminPlans } from '@/hooks/useAdminPlans';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import PlansManagementTab from '@/components/superadmin/PlansManagementTab';
import PlansPreviewTab from '@/components/superadmin/PlansPreviewTab';
import PlanSelectionSection from '@/components/subscription/PlanSelectionSection';
import SubscriptionCheckout from '@/components/subscription/SubscriptionCheckout';
import BusinessSignupPlansTab from '@/components/superadmin/BusinessSignupPlansTab';
import { PlanType } from '@/hooks/useAdminPlans';
import { useBusinessPlans } from '@/hooks/useBusinessPlans';

const AdminPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get business plans from the hook
  const { businessPlans, isLoading: isLoadingPlans } = useBusinessPlans();
  
  const {
    plans: adminPlans,
    isSaving,
    activeTab,
    isProcessing,
    selectedPlanId,
    selectedPlanType,
    fromSubscription,
    isTrialUser,
    showCheckout,
    setActiveTab,
    handleUpdatePlan,
    handleSavePlans,
    addNewPlan,
    deletePlan,
    handleSelectPlan,
    handlePurchasePlan,
    processPayment,
    handleBackToPlans,
    getSelectedPlan
  } = useAdminPlans();

  const {
    isLoading,
    user,
    handleLogout
  } = useAdminSettings();

  // Use business plans instead of reseller plans
  const [displayPlans, setDisplayPlans] = useState(adminPlans);
  
  useEffect(() => {
    // If business plans are available, use them instead of admin plans
    if (businessPlans && businessPlans.length > 0) {
      setDisplayPlans(businessPlans);
    } else {
      setDisplayPlans(adminPlans);
    }
  }, [businessPlans, adminPlans]);

  useEffect(() => {
    // Force display of plans if coming from subscription page
    if (location.state?.fromSubscription) {
      setActiveTab('preview');
    }
  }, [location.state, setActiveTab]);

  if (isLoading || isLoadingPlans) {
    return (
      <AdminLayout
        isLoading={true}
        onLogout={handleLogout}
        userRole={user?.role}
      >
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      isLoading={isLoading || isLoadingPlans}
      onLogout={handleLogout}
      userRole={user?.role}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold mb-6">Subscription Plans</h1>
        <p className="text-gray-600 mb-8">
          {fromSubscription 
            ? isTrialUser 
              ? "Choose a plan to continue after your trial period" 
              : "Choose a plan to activate your subscription"
            : "Manage subscription plans available to ReferMe.site customers"}
        </p>
      
        {fromSubscription ? (
          showCheckout ? (
            <SubscriptionCheckout
              selectedPlan={getSelectedPlan()}
              selectedPlanType={selectedPlanType}
              isProcessing={isProcessing}
              onBackToPlanSelection={handleBackToPlans}
              onProcessPayment={processPayment}
            />
          ) : (
            <PlanSelectionSection
              plans={displayPlans}
              selectedPlanId={selectedPlanId}
              selectedPlanType={selectedPlanType}
              isProcessing={isProcessing}
              isTrialUser={isTrialUser}
              onSelectPlan={handleSelectPlan}
              onPurchasePlan={handlePurchasePlan}
            />
          )
        ) : (
          <Tabs 
            defaultValue={activeTab} 
            onValueChange={setActiveTab} 
            className="w-full space-y-6"
          >
            <TabsList className="grid w-full grid-cols-3 bg-white border border-gray-200 shadow-sm">
              <TabsTrigger 
                value="plans" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                Subscription Plans
              </TabsTrigger>
              <TabsTrigger 
                value="business-signup" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                Business Signup Plans
              </TabsTrigger>
              <TabsTrigger 
                value="preview" 
                className="data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                Preview
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="plans" className="space-y-6">
              <PlansManagementTab
                plans={adminPlans}
                isSaving={isSaving}
                onUpdatePlan={handleUpdatePlan}
                onAddNewPlan={addNewPlan}
                onDeletePlan={deletePlan}
                onSavePlans={handleSavePlans}
              />
            </TabsContent>
            
            <TabsContent value="business-signup" className="space-y-6">
              <BusinessSignupPlansTab />
            </TabsContent>
            
            <TabsContent value="preview" className="space-y-6">
              <PlansPreviewTab plans={displayPlans} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPlans;
